import { IDropdownOption } from "@fluentui/react";
import { graphRoot, sitename } from "../constants/global-values";
import { dataURLtoBlob, japaneseDateConverter } from "../utils/item-helper";

export const getSiteAndListId = async (token: string, listname: string) => {
  const res = await fetch(`https://graph.microsoft.com/v1.0/sites?search=` + sitename, {
    headers: { Authorization: `Bearer ${token}` }
  });
  const data = await res.json();
  if (!data.value || !Array.isArray(data.value)) {
    console.error("Invalid response from /sites?search=" + sitename + ":", data);
    throw new Error("Unable to find site list. Check your site name or token.");
  }
  const site = data.value.find((s: { name: string; id: string }) => s.name === sitename);
  if (!site) {
    console.error("Site not found in search results", data.value);
    throw new Error("Site not found.");
  }
  const listRes = await fetch(`https://graph.microsoft.com/v1.0/sites/${site.id}/lists`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  const listData = await listRes.json();
  const list = listData.value?.find((l: { name: string; id: string }) => l.name === listname);
  if (!list) {
    throw new Error("List not found.");
  }
  return {
    siteId: site.id,
    listId: list.id
  };
};

export async function getListItems(token: string, siteId: string, listId: string) {
  const res = await fetch(`${graphRoot}/sites/${siteId}/lists/${listId}/items?expand=fields`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const json = await res.json();
  return json.value.map((item: any) => ({
    id: item.id,
    title: item.fields.Title,
    Name: item.fields.Name,
    DateOfBirth: item.fields.DateOfBirth ? new Date(item.fields.DateOfBirth).toISOString().split("T")[0] : null,
    Gender: item.fields.Gender,
    Address: item.fields.Address,
    PhoneNumber: item.fields.PhoneNumber,
    EmergencyPhone: item.fields.EmergencyPhone,
    EmergencyName: item.fields.EmergencyName,
    EmergencyRelation: item.fields.EmergencyRelation,
    PartnerCompany: item.fields.PartnerCompany,
    IsSoleProprietor: item.fields.IsSoleProprietor,
    SpecialLaborInsurance: item.fields.SpecialLaborInsurance,
    BloodType: item.fields.BloodType,
    HealthCheckDate: item.fields.HealthCheckDate ? new Date(item.fields.HealthCheckDate).toISOString().split("T")[0] : null,
    JobType: item.fields.JobType,
    Qualifications: item.fields.Qualifications,
    QualificationExpiry: item.fields.QualificationExpiry ? new Date(item.fields.QualificationExpiry).toISOString().split("T")[0] : null,
    CertificatePhotos: item.fields.CertificatePhotos
      ? `${item.fields.CertificatePhotos.Url}`
      : "",
    FacePhotos: item.fields.FacePhotos
      ? `${item.fields.FacePhotos.Url}`
      : "",
        PartnerName: item.fields.PartnerName,
  }));
}

export async function addItem(token: string, siteId: string, listId: string, data: any) {
  const payload = {
    fields: {
      Name: data.Name,
      DateOfBirth: data.DateOfBirth ? japaneseDateConverter(data.DateOfBirth) : null,
      Gender: data.Gender,
      Address: data.Address,
      PhoneNumber: data.PhoneNumber,
      EmergencyPhone: data.EmergencyPhone,
      EmergencyName: data.EmergencyName,
      EmergencyRelation: data.EmergencyRelation,
      IsSoleProprietor: data.IsSoleProprietor,
      SpecialLaborInsurance: data.SpecialLaborInsurance,
      BloodType: data.BloodType,
      PartnerName: data.PartnerName,
      HealthCheckDate: data.HealthCheckDate ? japaneseDateConverter(data.HealthCheckDate) : null,
      JobType: data.JobType,
      QualificationExpiry: data.QualificationExpiry ? japaneseDateConverter(data.QualificationExpiry) : null,
    }
  };
  const res = await fetch(`${graphRoot}/sites/${siteId}/lists/${listId}/items`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const error = await res.json();
    console.error("Add failed:", error);
    throw new Error(`Add item failed: ${res.status}`);
  }
  return await res.json();
}

export async function addItemwithFile(token: string, siteId: string, listId: string, data: any) {
  const payload: any = {
    fields: {
      Name: data.Name,
      DateOfBirth: data.DateOfBirth ? japaneseDateConverter(data.DateOfBirth) : null,
      Gender: data.Gender,
      Address: data.Address,
      PhoneNumber: data.PhoneNumber,
      EmergencyPhone: data.EmergencyPhone,
      EmergencyName: data.EmergencyName,
      EmergencyRelation: data.EmergencyRelation,
      IsSoleProprietor: data.IsSoleProprietor,
      SpecialLaborInsurance: data.SpecialLaborInsurance,
      BloodType: data.BloodType,
      HealthCheckDate: data.HealthCheckDate ? japaneseDateConverter(data.HealthCheckDate) : null,
      JobType: data.JobType,
       PartnerName: data.PartnerName,
      QualificationExpiry: data.QualificationExpiry ? japaneseDateConverter(data.QualificationExpiry) : null,
    },
  };
  if (data.CertificatePhotos instanceof File) {
    const driveId = await getDriveId(token, siteId, "Documents");
    const fileUrl = await uploadFile(siteId, driveId, data.CertificatePhotos, token);

    payload.fields.CertificatePhotos = `${fileUrl}, ${data.CertificatePhotos.name ?? "Certificate photo"}`;
  }
  else if (typeof data.CertificatePhotos === "string") {
    payload.fields.CertificatePhotos = `${data.CertificatePhotos}, Certificate photo`;
  }
  const res = await fetch(
    `${graphRoot}/sites/${siteId}/lists/${listId}/items`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    }
  );
  if (!res.ok) {
    const error = await res.json();
    console.error("Add failed:", error);
    throw new Error(`Add item failed: ${res.status}`);
  }
  return await res.json();
}

export async function getDriveId(token: string, siteId: string, libraryName: string) {
  const response = await fetch(
    `${graphRoot}/sites/${siteId}/drives`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  if (!response.ok) {
    throw new Error("Failed to fetch drives");
  }
  const data = await response.json();
  const drive = data.value.find((d: any) => d.name === libraryName);
  if (!drive) {
    throw new Error(`Library '${libraryName}' not found`);
  }
  return drive.id;
}

export async function uploadFile(siteId: string, driveId: string, file: File, token: string) {
  const response = await fetch(`${graphRoot}/sites/${siteId}/drives/${driveId}/root:/${file.name}:/content`,
    {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: file,
    }
  );
  if (!response.ok) {
    throw new Error("File upload failed");
  }
  const uploadedFile = await response.json();
  return uploadedFile.webUrl;
}

export async function updateItem(token: string, siteId: string, listId: string, itemId: string, data: any) {
  const payload = {
    Title: data.title,
    Name: data.Name,
    DateOfBirth: data.DateOfBirth ? japaneseDateConverter(data.DateOfBirth) : null,
    Gender: data.Gender,
    Address: data.Address,
    PhoneNumber: data.PhoneNumber,
    EmergencyPhone: data.EmergencyPhone,
    EmergencyName: data.EmergencyName,
    EmergencyRelation: data.EmergencyRelation,
    PartnerCompany: data.PartnerCompany,
    IsSoleProprietor: data.IsSoleProprietor,
    SpecialLaborInsurance: data.SpecialLaborInsurance,
    BloodType: data.BloodType,
    PartnerName: data.PartnerName,
    HealthCheckDate: data.HealthCheckDate ? japaneseDateConverter(data.HealthCheckDate) : null,
    JobType: data.JobType,
    QualificationExpiry: data.QualificationExpiry ? japaneseDateConverter(data.QualificationExpiry) : null
  };
  await fetch(`${graphRoot}/sites/${siteId}/lists/${listId}/items/${itemId}/fields`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

export async function deleteItem(token: string, siteId: string, listId: string, itemId: string) {
  if (token != null) {
    const res = await fetch(`${graphRoot}/sites/${siteId}/lists/${listId}/items/${itemId}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      const error = await res.json();
      console.error("Delete failed:", error);
      throw new Error(`Delete item failed: ${res.status}`);
    }
  }
}

export const getItemById = async (token: string, siteId: string, listId: string, itemId: string) => {
  const res = await fetch(
    `${graphRoot}/sites/${siteId}/lists/${listId}/items/${itemId}?expand=fields`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  const data = await res.json();
  return data.fields;
};

export const getDropdownOptionsFromList = async (
  token: string,
  siteId: string,
  listId: string,
  textFieldName: string,
  keyFieldName: string
): Promise<IDropdownOption[]> => {
  const res = await fetch(
    `${graphRoot}/sites/${siteId}/lists/${listId}/items?expand=fields`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  const data = await res.json();
  return data.value.map((item: any) => ({
    key: item.fields[keyFieldName],
    text: item.fields[textFieldName],
  }));
};

export const getNamesByIds = async (
  token: string,
  siteId: string,
  listId: string,
  ids: string[],
  nameField: string
): Promise<string[]> => {
  const url = `${graphRoot}/sites/${siteId}/lists/${listId}/items?$expand=fields&$top=999`;
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = await res.json();
  const idSet = new Set(ids.map(id => id.trim()));
  return data.value
    .filter((item: any) => idSet.has(item.id.toString()))
    .map((item: any) => item.fields[nameField]);
};

export async function AddItemCommon(
  token: string,
  siteId: string,
  listId: string,
  data: Record<string, any>
) {
  // Handle Japanese date conversion automatically
  const convertedFields: Record<string, any> = {};
  Object.entries(data).forEach(([key, value]) => {
    if (value instanceof Date) {
      // If field is Date object → convert to Japanese format
      convertedFields[key] = japaneseDateConverter(value);
    } else if (typeof value === "string" && value.match(/^\d{4}-\d{2}-\d{2}$/)) {
      // If field is ISO date string → also convert
      convertedFields[key] = japaneseDateConverter(value);
    } else {
      // Otherwise keep as is
      convertedFields[key] = value;
    }
  });
  const payload = { fields: convertedFields };
  const response = await fetch(
    `${graphRoot}/sites/${siteId}/lists/${listId}/items`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    }
  );
  if (!response.ok) {
    throw new Error(`Failed to add item: ${response.status} ${response.statusText}`);
  }
  return await response.json();
};

export async function addSiteEntry(token: string, siteId: string, listId: string, data: any) {
  const payload = {
    fields: {
      // ContractId: data.ContractId,
      // OnSiteId: data.OnSiteId,
      EntryDate: data.EntryDate,
      WorkerIdLookupId: data.WorkerId,
      Role: data.Role,
      // CoopCompanyLinkId: data.CoopCompanyLinkId,
      ConditionNote: data.ConditionNote,
      InjuryNote: data.InjuryNote,
      BloodPressure: data.BloodPressure,
      BodyTemperature: 78,
      OnsitePartnerRelationID: data.PartnerName,
      //Signature: data.Signature,
      // EntryTrainingCompleted: data.EntryTrainingCompleted
    }
  };
  const res = await fetch(`${graphRoot}/sites/${siteId}/lists/${listId}/items`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const error = await res.json();
    console.error("Add failed:", error);
    throw new Error(`Add item failed: ${res.status}`);
  }
  return await res.json();
}
export async function signature_Poc(
  token: string,
  siteId: string,
  listId: string,
  data: any
) {
  let signatureField = {};
  if (data.Signature) {
    const blob = dataURLtoBlob(data.SignatureData);
    const fileName = `signature_${Date.now()}.png`;
    const uploadUrl = `${graphRoot}/sites/${siteId}/drive/root:/Documents/Signatures/${fileName}:/content`;
    const uploadRes = await fetch(uploadUrl, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "image/png",
      },
      body: blob,
    });
    console.log("Upload response status:", uploadRes.status);
    if (!uploadRes.ok) {
      const err = await uploadRes.text();
      throw new Error("Signature upload failed: " + err);
    }
    const fileMeta = await uploadRes.json();
    console.log("Uploaded signature file:", fileMeta);
    signatureField = {
      Signature: {
        fileName: fileMeta.name,
        url: fileMeta.webUrl,
        id: fileMeta.id,
      },
    };

    delete data.SignatureData;
  }
  const payload = {
    fields: {
      // ContractId: data.ContractId,
      // OnSiteId: data.OnSiteId,
      EntryDate: data.EntryDate,
      WorkerIdLookupId: data.WorkerId,
      Role: data.Role,
      // CoopCompanyLinkId: data.CoopCompanyLinkId,
      ConditionNote: data.ConditionNote,
      InjuryNote: data.InjuryNote,
      BloodPressure: data.BloodPressure,
      BodyTemperature: 78,
      ...signatureField,
      //Signature: data.Signature,
      // EntryTrainingCompleted: data.EntryTrainingCompleted
    }
  };
  const res = await fetch(
    `${graphRoot}/sites/${siteId}/lists/${listId}/items`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    }
  );
  if (!res.ok) {
    const error = await res.json();
    console.error("Add failed:", error);
    throw new Error(`Add item failed: ${res.status}`);
  }
  return await res.json();
}

export async function siteEntry_List(token: string, siteId: string, listId: string) {
  const res = await fetch(`${graphRoot}/sites/${siteId}/lists/${listId}/items?expand=fields`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const json = await res.json();
  return json.value.map((item: any) => ({
    id: item.id,
    WorkerIdLookupId: item.fields.WorkerIdLookupId,
    EntryTrainingCompleted: item.fields.EntryTrainingCompleted
  }));
}